"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Ticket, Building2, UtensilsCrossed, Plane, PlaneTakeoff, Navigation } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const destinations = [{
  id: 1,
  time: "10-15",
  unit: "min",
  name: "Disneyland",
  desc: "Easy freeway access to the parks",
  icon: Ticket
}, {
  id: 2,
  time: "13",
  unit: "min",
  name: "Knott's Berry Farm",
  desc: "Quick drive to thrilling coasters",
  icon: Ticket
}, {
  id: 3,
  time: "15",
  unit: "min",
  name: "Anaheim Convention Center",
  desc: "Perfect for expos and events",
  icon: Building2
}, {
  id: 4,
  time: "5",
  unit: "min",
  name: "Little Saigon",
  desc: "Incredible dining and culture nearby",
  icon: UtensilsCrossed
}, {
  id: 5,
  time: "15",
  unit: "min",
  name: "John Wayne Airport (SNA)",
  desc: "Most convenient local airport",
  icon: Plane
}, {
  id: 6,
  time: "15",
  unit: "min",
  name: "Long Beach Airport (LGB)",
  desc: "Easy alternative for regional flights",
  icon: Plane
}, {
  id: 7,
  time: "40",
  unit: "min",
  name: "LAX Airport",
  desc: "Major international travel hub",
  icon: PlaneTakeoff
}];
export default function MinutesFromTheMagic() {
  const {
    ref: mapRef,
    isVisible: isMapVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: timelineRef,
    isVisible: isTimelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="minutes-from-the-magic" className="dark bg-background text-foreground py-16 lg:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          
          {}
          <div ref={mapRef} className={`lg:col-span-7 relative transition-all duration-1000 ease-out ${isMapVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative rounded-[2rem] overflow-hidden border border-border/50 bg-secondary/20 shadow-2xl aspect-[4/3] lg:aspect-auto lg:h-[700px] group">
              {}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-chart-2/10 via-background/50 to-background z-10 pointer-events-none" />
              
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1596668/img/murrieta_socal_stylized_map.png" alt="Stylized map showing Westminster’s proximity to Disneyland and Knott’s Berry Farm" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />

              {}
              <div className="absolute bottom-6 left-6 right-6 md:bottom-8 md:left-8 md:right-auto z-20">
                <div className="bg-card/90 backdrop-blur-md border border-border/50 rounded-2xl p-5 shadow-xl flex items-start gap-4 max-w-sm">
                  <div className="bg-primary/20 p-3 rounded-full shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-card-foreground">Westminster, CA near Disney and Knotts</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      I rent out 4 individual private rooms (not whole house) inside a quiet, comfortable home. Each room has its own king or queen bed with fresh sheets, portable AC and heater.
                    </p>
                    <p className="text-sm font-medium text-primary mt-2">
                      $50 on weekdays and $55 on weekends
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={timelineRef} className={`lg:col-span-5 transition-all duration-1000 delay-200 ease-out ${isTimelineVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="bg-secondary/30 rounded-[2.5rem] p-8 md:p-10 lg:p-12 border border-border/50 relative overflow-hidden">
              {}
              <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-chart-2/10 rounded-full blur-3xl pointer-events-none" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <Navigation className="w-5 h-5 text-chart-2" />
                  <span className="text-sm font-semibold tracking-wider uppercase text-chart-2">Location Highlights</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground mb-10 leading-tight">
                  Minutes From <br />
                  <span className="text-primary">The Magic</span>
                </h2>

                <div className="relative border-l-2 border-border pl-6 ml-3 space-y-8 py-2">
                  {destinations.map((item, i) => <div key={item.id} data-index={i} className="relative group">
                      {}
                      <div className="absolute -left-[31px] top-2 flex h-4 w-4 items-center justify-center rounded-full bg-background border-2 border-border group-hover:border-chart-2 transition-colors duration-300 ring-4 ring-secondary/30">
                        <div className="h-1.5 w-1.5 rounded-full bg-border group-hover:bg-chart-2 transition-colors duration-300" />
                      </div>

                      {}
                      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-5">
                        <div className="flex flex-col items-start sm:items-end min-w-[4.5rem] pt-0.5">
                          <span className="text-3xl md:text-4xl font-bold text-chart-2 leading-none tracking-tighter">
                            {item.time}
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1">
                            {item.unit}
                          </span>
                        </div>
                        
                        <div className="pt-1">
                          <h4 className="text-lg font-semibold text-foreground flex items-center gap-2">
                            <item.icon className="w-4 h-4 text-muted-foreground group-hover:text-chart-2 transition-colors duration-300" />
                            {item.name}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}